/**
 * Auth hero image cache — removes the /login + /signup hero flicker.
 *
 * Problem: the right-hand hero image comes from the public
 * `/api/branding/lookup`. Until that request resolves the pages had no
 * image URL and rendered the built-in gradient "mock hero" (dark blue /
 * violet), then swapped to the configured image a moment later.
 *
 * Fix (with the pages): remember the last resolved hero URLs per site
 * (host + org slug hint) so repeat visits paint the configured image
 * immediately, and render a neutral panel — never the mock — while the
 * first lookup is still in flight. Nothing is hardcoded: the cache only
 * ever holds what the branding endpoint returned, and is refreshed (or
 * cleared) on every lookup.
 */
const KEY = "analyzio_auth_hero_cache";

function siteKey({ host = "", slug = "" } = {}) {
    const h = host || (typeof window !== "undefined" ? window.location.host : "");
    return `${h}|${slug || ""}`;
}

/** @returns {{login_image_url: string|null, signup_image_url: string|null} | null} */
export function readAuthHero(hints) {
    try {
        const all = JSON.parse(localStorage.getItem(KEY) || "{}");
        const v = all[siteKey(hints)];
        if (!v || typeof v !== "object") return null;
        return {
            login_image_url: v.login_image_url || null,
            signup_image_url: v.signup_image_url || null,
        };
    } catch {
        return null;
    }
}

// Hero images may be stored inline as base64 `data:` URIs (~1 MB+). Keep
// the cache bounded so it never crowds out other localStorage state: only
// the CURRENT site's entry is kept, and oversized payloads aren't cached.
const MAX_CACHE_CHARS = 2_500_000;

/** Persist the hero URLs from a pre-email `/branding/lookup` response. */
export function writeAuthHero(hints, brand) {
    try {
        const entry = {
            login_image_url: brand?.login_image_url || null,
            signup_image_url: brand?.signup_image_url || null,
        };
        const payload = JSON.stringify({ [siteKey(hints)]: entry });
        if (payload.length > MAX_CACHE_CHARS) {
            localStorage.removeItem(KEY);
            return;
        }
        localStorage.setItem(KEY, payload);
    } catch {
        // Quota exceeded / storage unavailable — cache is best-effort.
        try { localStorage.removeItem(KEY); } catch { /* ignore */ }
    }
}
